import {
  COCKTAIL_STATUSES,
  INGREDIENT_CATEGORIES,
  TECHNIQUES,
  UNITS,
  type Cocktail,
  type Ingredient,
  type Settings,
  type Tasting,
  type Version,
  type VersionIngredient,
} from './types.js';

export const BACKUP_FORMAT = 'muddle-backup';
export const BACKUP_FORMAT_VERSION = 1;

/** Everything in the database, including archived rows, as plain JSON. */
export interface BackupData {
  format: typeof BACKUP_FORMAT;
  formatVersion: number;
  exportedAt: string;
  settings: Partial<Settings>;
  ingredients: Ingredient[];
  cocktails: Cocktail[];
  versions: Version[];
  versionIngredients: VersionIngredient[];
  tastings: Tasting[];
}

export interface BackupCounts {
  ingredients: number;
  cocktails: number;
  versions: number;
  tastings: number;
}

export const backupCounts = (d: BackupData): BackupCounts => ({
  ingredients: d.ingredients.length,
  cocktails: d.cocktails.length,
  versions: d.versions.length,
  tastings: d.tastings.length,
});

type Row = Record<string, unknown>;
const isRow = (v: unknown): v is Row => typeof v === 'object' && v !== null && !Array.isArray(v);
const MAX_ERRORS = 10;

/**
 * Strict check of an imported backup: shapes, enums, unique ids and every reference.
 * Returns human-readable problems (at most 10); an empty list means safe to import.
 */
export function validateBackup(raw: unknown): { data?: BackupData; errors: string[] } {
  const errors: string[] = [];
  const err = (m: string) => {
    if (errors.length < MAX_ERRORS) errors.push(m);
  };
  if (!isRow(raw) || raw.format !== BACKUP_FORMAT) {
    return { errors: ['This is not a Muddle backup file.'] };
  }
  if (typeof raw.formatVersion !== 'number' || raw.formatVersion > BACKUP_FORMAT_VERSION) {
    return {
      errors: ['This backup was made by a newer version of Muddle. Update the app and try again.'],
    };
  }
  const list = (key: string): Row[] => {
    const v = raw[key];
    if (!Array.isArray(v)) {
      err(`Missing "${key}" list.`);
      return [];
    }
    return v.filter((r, i): r is Row => {
      if (!isRow(r)) err(`${key}[${i}] is not an object.`);
      return isRow(r);
    });
  };
  const ingredients = list('ingredients');
  const cocktails = list('cocktails');
  const versions = list('versions');
  const lines = list('versionIngredients');
  const tastings = list('tastings');

  const str = (r: Row, k: string, where: string, allowEmpty = false) => {
    if (typeof r[k] !== 'string' || (!allowEmpty && r[k] === ''))
      err(`${where}: "${k}" must be text.`);
  };
  const num = (r: Row, k: string, where: string, min = -Infinity, max = Infinity) => {
    const v = r[k];
    if (typeof v !== 'number' || !Number.isFinite(v) || v < min || v > max)
      err(`${where}: "${k}" must be a number.`);
  };
  const oneOf = (r: Row, k: string, allowed: readonly string[], where: string) => {
    if (!allowed.includes(r[k] as string)) err(`${where}: "${k}" has an unknown value.`);
  };
  const optStr = (r: Row, k: string, where: string) => {
    if (r[k] !== null && r[k] !== undefined && typeof r[k] !== 'string')
      err(`${where}: "${k}" must be text or empty.`);
  };
  const ids = (rows: Row[], what: string) => {
    const seen = new Set<string>();
    rows.forEach((r, i) => {
      str(r, 'id', `${what}[${i}]`);
      if (typeof r.id === 'string') {
        if (seen.has(r.id)) err(`${what}: duplicate id "${r.id}".`);
        seen.add(r.id);
      }
    });
    return seen;
  };

  const ingredientIds = ids(ingredients, 'ingredients');
  ingredients.forEach((r, i) => {
    const w = `ingredients[${i}]`;
    str(r, 'name', w);
    oneOf(r, 'category', INGREDIENT_CATEGORIES, w);
    num(r, 'abvPct', w, 0, 100);
    str(r, 'notes', w, true);
    optStr(r, 'archivedAt', w);
  });
  const cocktailIds = ids(cocktails, 'cocktails');
  cocktails.forEach((r, i) => {
    const w = `cocktails[${i}]`;
    str(r, 'name', w);
    oneOf(r, 'status', COCKTAIL_STATUSES, w);
    str(r, 'createdAt', w);
    str(r, 'updatedAt', w);
    optStr(r, 'archivedAt', w);
    if (!Array.isArray(r.tags) || r.tags.some((t) => typeof t !== 'string'))
      err(`${w}: "tags" must be a list of text.`);
    if (r.parentId != null && !cocktailIds.has(r.parentId as string))
      err(`${w}: parent drink not found.`);
  });
  const versionIds = ids(versions, 'versions');
  const numbers = new Set<string>();
  versions.forEach((r, i) => {
    const w = `versions[${i}]`;
    if (!cocktailIds.has(r.cocktailId as string)) err(`${w}: its drink is missing.`);
    num(r, 'versionNumber', w, 1);
    const key = `${String(r.cocktailId)}#${String(r.versionNumber)}`;
    if (numbers.has(key)) err(`${w}: duplicate version number.`);
    numbers.add(key);
    oneOf(r, 'technique', TECHNIQUES, w);
    for (const k of ['glass', 'ice', 'garnish', 'methodNotes', 'changeNote']) str(r, k, w, true);
    str(r, 'createdAt', w);
  });
  ids(lines, 'versionIngredients');
  lines.forEach((r, i) => {
    const w = `versionIngredients[${i}]`;
    if (!versionIds.has(r.versionId as string)) err(`${w}: its version is missing.`);
    if (!ingredientIds.has(r.ingredientId as string)) err(`${w}: its ingredient is missing.`);
    num(r, 'amount', w, 0);
    oneOf(r, 'unit', UNITS, w);
    num(r, 'sortOrder', w, 0);
  });
  ids(tastings, 'tastings');
  tastings.forEach((r, i) => {
    const w = `tastings[${i}]`;
    if (!versionIds.has(r.versionId as string)) err(`${w}: its version is missing.`);
    str(r, 'date', w);
    for (const k of ['rating', 'sweet', 'sour', 'bitter', 'strong']) num(r, k, w, 1, 5);
    str(r, 'notes', w, true);
    optStr(r, 'archivedAt', w);
  });
  if (raw.settings !== undefined && !isRow(raw.settings)) err('"settings" must be an object.');
  if (errors.length) return { errors };

  return {
    errors,
    data: {
      format: BACKUP_FORMAT,
      formatVersion: raw.formatVersion as number,
      exportedAt: typeof raw.exportedAt === 'string' ? raw.exportedAt : new Date().toISOString(),
      settings: (raw.settings ?? {}) as Partial<Settings>,
      ingredients: ingredients as unknown as Ingredient[],
      cocktails: cocktails as unknown as Cocktail[],
      versions: versions as unknown as Version[],
      versionIngredients: lines as unknown as VersionIngredient[],
      tastings: tastings as unknown as Tasting[],
    },
  };
}
